import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Mail } from 'lucide-react';

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('adminToken', data.token);
        navigate('/admin/dashboard');
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0.95) 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative'
    }}>
      {/* Back to Website Button */}
      <a
        href="/"
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          padding: '12px 24px',
          background: 'rgba(212, 175, 55, 0.1)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          color: '#D4AF37',
          fontSize: '14px',
          fontWeight: '600',
          textDecoration: 'none',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#D4AF37';
          e.currentTarget.style.background = 'rgba(212, 175, 55, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
          e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
        }}
      >
        ← Back to Website
      </a>

      {/* Centered Skull Warning - Above Login Box */}
      <div style={{
        textAlign: 'center',
        marginBottom: '48px'
      }}>
        <img 
          src="https://customer-assets.emergentagent.com/job_wolf-eye/artifacts/mnp1umyq_skull-laugh.gif" 
          alt="Warning"
          style={{
            width: '80px',
            height: '80px',
            marginBottom: '20px',
            opacity: 0.9,
            display: 'block',
            margin: '0 auto 20px'
          }}
        />
        <div style={{
          fontSize: '15px',
          color: '#FF6B6B',
          fontWeight: '700',
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          marginBottom: '12px',
          textShadow: '0 0 12px rgba(255, 107, 107, 0.6)'
        }}>
          ⚠ CLASSIFIED ACCESS ⚠
        </div>
        <div style={{
          fontSize: '13px',
          color: 'rgba(255, 107, 107, 0.85)',
          letterSpacing: '0.5px',
          lineHeight: '1.5'
        }}>
          Unauthorized access will be prosecuted
        </div>
      </div>

      <div style={{
        maxWidth: '450px',
        width: '100%',
        background: 'rgba(0, 0, 0, 0.8)',
        border: '1px solid rgba(212, 175, 55, 0.3)',
        padding: '48px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Shield size={48} style={{ color: '#D4AF37', margin: '0 auto 16px' }} />
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#FFFFFF', marginBottom: '8px' }}>
            Admin Portal
          </h1>
          <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>
            Labana Dynamics OÜ
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            color: '#FF6B6B',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              fontSize: '12px',
              fontWeight: '700',
              color: '#D4AF37',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: '8px',
              display: 'block'
            }}>
              Email
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
              <input
                type="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                required
                placeholder="admin@labanadynamics.com"
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 48px',
                  background: '#000000',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={{
              fontSize: '12px',
              fontWeight: '700',
              color: '#D4AF37',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              marginBottom: '8px',
              display: 'block'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                placeholder="Enter password"
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 48px',
                  background: '#000000',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
              border: 'none',
              color: '#000000',
              fontSize: '14px',
              fontWeight: '700',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Access Dashboard
          </button>
        </form>

        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'rgba(212, 175, 55, 0.05)',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <strong style={{ color: '#D4AF37' }}>Super Admin Credentials:</strong><br/>
          Email: admin@labanadynamics.com<br/>
          Password: WolfEye2025!SecureAdmin
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;